import React, { useEffect, useMemo, useRef, useState } from "react"
import DeleteIcon from "@mui/icons-material/Delete"
import { useTranslation } from "react-i18next"
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Slider,
    Typography,
    Button,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded"
import DataService from "../services/data"
import { Color } from "@front-apps/core/models/colors"
import ActionType from "@front-apps/core/models/actionType"
import ComponentsUtils from "@front-apps/core/utils/components"
import CwMultiUnitInput from "./cw-multiUnitInput "
import speed1 from "../assets/img/Vitesse Niveau 1.png"
import speed2 from "../assets/img/Vitesse Niveau 2.png"
import speed3 from "../assets/img/Vitesse Niveau 3.png"
import speed4 from "../assets/img/Vitesse Niveau 4.png"
import speed5 from "../assets/img/Vitesse Niveau 5.png"
import skier1 from "../assets/img/Skieur Niveau 1 (Enfant) .png"
import skier2 from "../assets/img/Skieur Niveau 2.png"
import skier3 from "../assets/img/Skieur Niveau 3.png"
import skier4 from "../assets/img/Skieur Niveau 4.png"
import skier5 from "../assets/img/Skieur Niveau 5.png"
import WebNavigationParamService from "@front-apps/core/services/webNavigationParam"
import StoreService from "@front-apps/core/services/store"
import GroupService from "@front-apps/core/services/group"
import NavigationService from "@front-apps/core/services/navigation"
import CwIcon from "@front-apps/core/components/web/cw-icon"
import UIService from "@front-apps/core/services/UIService"
import ActionService from "@front-apps/core/services/action"

interface User {
    name: string
    age: number | string
    weight: number | string
    height: number | string
    shoeSize: number | string
    skiLevel: number
}

const CwGroupNestedList = () => {
    const [users, setUsers] = useState<User[]>([])
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [userToDelete, setUserToDelete] = useState<number | null>(null)
    const [isFormComplete, setIsFormComplete] = useState(false)
    const [nameErrors, setNameErrors] = useState<boolean[]>([])
    const [formFields, setFormFields] = useState<any[]>([])
    const [skiLevelTouched, setSkiLevelTouched] = useState<boolean[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const hasSkippedRef = useRef(false)
    const { t } = useTranslation()

    const formFieldMap = useMemo(() => {
        const map: Record<string, { show: boolean; required: boolean }> = {}
        formFields.forEach((field) => {
            map[field._id] = { show: field.show, required: field.required }
        })
        return map
    }, [formFields])

    const handleUserChange = <T extends keyof User>(
        index: number,
        key: T,
        value: User[T]
    ) => {
        const updatedUsers = [...users]
        updatedUsers[index][key] = value
        setUsers(updatedUsers)
    }

    const addUser = () => {
        setUsers([
            ...users,
            {
                name: "",
                age: "",
                weight: "",
                height: "",
                shoeSize: "",
                skiLevel: 1,
            },
        ])
        setNameErrors((prev) => [...prev, false])
        setSkiLevelTouched((prev) => [...prev, false])
        setExpandedIndex(users.length)
    }

    const confirmDeleteUser = () => {
        if (userToDelete !== null) {
            const updatedUsers = [...users]
            updatedUsers.splice(userToDelete, 1)
            setUsers(updatedUsers)
            setNameErrors((prev) => prev.filter((_, i) => i !== userToDelete))
            setSkiLevelTouched((prev) =>
                prev.filter((_, i) => i !== userToDelete)
            )
            setDeleteModalOpen(false)
            setUserToDelete(null)
        }
    }

    const openDeleteModal = (index: number) => {
        setUserToDelete(index)
        setDeleteModalOpen(true)
    }

    const cancelDelete = () => {
        setDeleteModalOpen(false)
        setUserToDelete(null)
    }

    const validateNames = () => {
        const errors = users.map(
            (user, index) =>
                users.filter(
                    (u) =>
                        u.name.toLocaleLowerCase().trim() ===
                        user.name.toLocaleLowerCase().trim()
                ).length > 1
        )

        setNameErrors(errors)
        return !errors.includes(true)
    }

    useEffect(() => {
        const fetchAndSetMainCustomer = async () => {
            const customer = await DataService.getInstance().getCustomer()
            const mainName = customer.split(":")[0]
            setUsers([
                {
                    name: mainName,
                    age: "",
                    weight: "",
                    height: "",
                    shoeSize: "",
                    skiLevel: 1,
                },
            ])
            setNameErrors([false])
            setSkiLevelTouched([false])
            setExpandedIndex(0)
        }

        fetchAndSetMainCustomer()
        fetchFormFields()
    }, [])

    useEffect(() => {
        const allFieldsComplete =
            users.every((user, userIndex) => {
                return Object.entries(formFieldMap).every(([key, config]) => {
                    const value = user[key as keyof User]

                    if (!config.show) return true

                    // Special case: skiLevel must be actively selected if required
                    if (
                        key === "skiLevel" &&
                        config.required &&
                        !skiLevelTouched[userIndex]
                    ) {
                        return false
                    }

                    const isEmpty =
                        value === undefined ||
                        value === "" ||
                        (typeof value === "number" && isNaN(value))

                    if (config.required && isEmpty) return false

                    if (!config.required && isEmpty) return true

                    switch (key) {
                        case "height":
                        case "weight":
                        case "shoeSize":
                        case "age": {
                            const num = Number(value)
                            return !isNaN(num) && num > 0
                        }
                        default:
                            return true
                    }
                })
            }) &&
            users.length > 0 &&
            validateNames()

        setIsFormComplete(allFieldsComplete)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, formFieldMap, skiLevelTouched])

    const fetchFormFields = async () => {
        const webNavigationParam = WebNavigationParamService.getInstance()
        const { params } = webNavigationParam.get()
        let groupId = StoreService.getInstance().local.get("groupId")
        let pdv = StoreService.getInstance().local.get("pdv")

        if (!groupId) {
            groupId = params?.groupId
        }
        if (!pdv) {
            pdv = params?.pdv || 0
        }
        try {
            const groupCheckInSetting =
                await GroupService.getInstance().getSetting("checkIn", groupId)

            const formFields = groupCheckInSetting?.pdvs[pdv]?.groupForm || []
            setFormFields(formFields)
            const allHidden = formFields.every((field: any) => !field.show)
            if (allHidden) {
                if (!hasSkippedRef.current) {
                    hasSkippedRef.current = true
                    handleNext()
                }
            } else {
                setIsLoading(false)
            }
        } catch (error) {
            console.error("Error fetching form fields:", error)
            NavigationService.getInstance().navigateTo("error", {
                groupId,
            })
        }
    }

    const handleNext = () => {
        ActionService.exec([
            {
                actionType: ActionType.CALLBACK,
                actionParams: {
                    functionKey: "DataService.addGroupDataToQRCodeTram",
                    params: [users],
                },
            },
            {
                actionType: ActionType.CALLBACK,
                actionParams: {
                    functionKey: "LinkService.createShortLink",
                    params: ["{{#navParam.groupId}}", "{{#navParam.pdv}}"],
                },
            },
            {
                actionType: ActionType.CALLBACK,
                actionParams: {
                    functionKey: "DialogService.send",
                    params: [
                        {
                            type: "config",
                            params: [
                                {
                                    action: "next",
                                },
                            ],
                            sender: "validGroupButton",
                            to: "stepperCheckIn",
                        },
                    ],
                },
            },
        ])
    }

    return isLoading ? (
        ComponentsUtils.renderLoader()
    ) : (
        <Box
            display="flex"
            flexDirection="column"
            gap={2}
            className="groupListContainer"
        >
            {users.map((user, index) => (
                <Accordion
                    key={index}
                    expanded={expandedIndex === index}
                    onChange={() =>
                        setExpandedIndex(expandedIndex === index ? null : index)
                    }
                >
                    <AccordionSummary
                        expandIcon={
                            <KeyboardArrowDownRoundedIcon
                                fontSize="large"
                                className="userExpandIcon"
                            />
                        }
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent={
                                formFieldMap.name?.show
                                    ? "space-between"
                                    : "flex-end"
                            }
                            width="100%"
                        >
                            {formFieldMap.name?.show &&
                                (expandedIndex === index ? (
                                    <TextField
                                        variant="outlined"
                                        value={user.name}
                                        onChange={(e) => {
                                            handleUserChange(
                                                index,
                                                "name",
                                                e.target.value
                                            )
                                            validateNames()
                                        }}
                                        label={t("first_name")}
                                        size="small"
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                        className="userNameSummaryExpanded"
                                        error={nameErrors[index]}
                                        helperText={
                                            nameErrors[index] &&
                                            t("usersCantHaveSameName")
                                        }
                                        required={formFieldMap.name.required}
                                    />
                                ) : (
                                    <Box className="errorMessageContainer">
                                        <Typography
                                            variant="body1"
                                            onClick={() =>
                                                setExpandedIndex(index)
                                            }
                                        >
                                            {user.name || t("last_name")}
                                        </Typography>
                                        {nameErrors[index] && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                            >
                                                {t("usersCantHaveSameName")}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}

                            <Button
                                variant="contained"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    openDeleteModal(index)
                                }}
                                className="checkInButton removeUserButton"
                                startIcon={<DeleteIcon />}
                            ></Button>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box display="flex" flexDirection="column" gap={2}>
                            {formFieldMap.age?.show && (
                                <TextField
                                    label={t("age")}
                                    type="number"
                                    value={user.age}
                                    onChange={(e) =>
                                        handleUserChange(
                                            index,
                                            "age",
                                            e.target.value as string
                                        )
                                    }
                                    variant="outlined"
                                    fullWidth
                                    required={formFieldMap.age.required}
                                    error={
                                        user.age !== "" && Number(user.age) <= 0
                                    }
                                    helperText={
                                        user.age !== "" &&
                                        Number(user.age) <= 0 &&
                                        t("ageError")
                                    }
                                />
                            )}

                            {/* Weight */}
                            {formFieldMap.weight?.show && (
                                <CwMultiUnitInput
                                    name="weightInput"
                                    type="multiUnitInput"
                                    config={{
                                        unit: "weight",
                                        onChange: (value: any) => {
                                            handleUserChange(
                                                index,
                                                "weight",
                                                value.value
                                            )
                                        },
                                        required: formFieldMap.weight.required,
                                        error:
                                            user.weight !== "" &&
                                            Number(user.weight) <= 0,
                                        helperText:
                                            user.weight !== "" &&
                                            Number(user.weight) <= 0 &&
                                            (t("weightError") as string),
                                    }}
                                />
                            )}
                            {/* Height */}
                            {formFieldMap.height?.show && (
                                <CwMultiUnitInput
                                    name="heightInput"
                                    type="multiUnitInput"
                                    config={{
                                        unit: "height",
                                        onChange: (value: any) => {
                                            handleUserChange(
                                                index,
                                                "height",
                                                value.value
                                            )
                                        },
                                        required: formFieldMap.height.required,
                                        error:
                                            user.height !== "" &&
                                            Number(user.height) <= 0,
                                        helperText:
                                            user.height !== "" &&
                                            Number(user.height) <= 0 &&
                                            (t("heightError") as string),
                                    }}
                                />
                            )}
                            {/* Shoe Size */}
                            {formFieldMap.shoeSize?.show && (
                                <CwMultiUnitInput
                                    name="shoeSizeInput"
                                    type="multiUnitInput"
                                    config={{
                                        unit: "shoeSize",
                                        onChange: (value: any) => {
                                            handleUserChange(
                                                index,
                                                "shoeSize",
                                                value.value
                                            )
                                        },
                                        required:
                                            formFieldMap.shoeSize.required,
                                        error:
                                            user.shoeSize !== "" &&
                                            Number(user.shoeSize) <= 0,
                                        helperText:
                                            user.shoeSize !== "" &&
                                            Number(user.shoeSize) <= 0 &&
                                            (t("shoeSizeError") as string),
                                    }}
                                />
                            )}
                            {formFieldMap.skiLevel?.show && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={1}
                                    alignItems="center"
                                >
                                    <Typography className="sliderLabel">
                                        {t("skiLevel")}
                                    </Typography>
                                    <Slider
                                        value={user.skiLevel}
                                        onChange={(e, newValue) => {
                                            handleUserChange(
                                                index,
                                                "skiLevel",
                                                newValue as number
                                            )
                                            setSkiLevelTouched((prev) => {
                                                const next = [...prev]
                                                next[index] = true
                                                return next
                                            })
                                        }}
                                        step={1}
                                        className="skiLevelSlider"
                                        marks={[
                                            {
                                                value: 1,
                                                label: (
                                                    <Box
                                                        display={"flex"}
                                                        justifyContent={
                                                            "center"
                                                        }
                                                        alignContent={"center"}
                                                        flexDirection={"column"}
                                                        gap={1}
                                                    >
                                                        <img
                                                            src={skier1}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                        <img
                                                            src={speed1}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                    </Box>
                                                ),
                                            },
                                            {
                                                value: 2,
                                                label: (
                                                    <Box
                                                        display={"flex"}
                                                        justifyContent={
                                                            "center"
                                                        }
                                                        alignContent={"center"}
                                                        flexDirection={"column"}
                                                        gap={1}
                                                    >
                                                        <img
                                                            src={skier2}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                        <img
                                                            src={speed2}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                    </Box>
                                                ),
                                            },
                                            {
                                                value: 3,
                                                label: (
                                                    <Box
                                                        display={"flex"}
                                                        justifyContent={
                                                            "center"
                                                        }
                                                        alignContent={"center"}
                                                        flexDirection={"column"}
                                                        gap={1}
                                                    >
                                                        <img
                                                            src={skier3}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                        <img
                                                            src={speed3}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                    </Box>
                                                ),
                                            },
                                            {
                                                value: 4,
                                                label: (
                                                    <Box
                                                        display={"flex"}
                                                        justifyContent={
                                                            "center"
                                                        }
                                                        alignContent={"center"}
                                                        flexDirection={"column"}
                                                        gap={1}
                                                    >
                                                        <img
                                                            src={skier4}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                        <img
                                                            src={speed4}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                    </Box>
                                                ),
                                            },
                                            {
                                                value: 5,
                                                label: (
                                                    <Box
                                                        display={"flex"}
                                                        justifyContent={
                                                            "center"
                                                        }
                                                        alignContent={"center"}
                                                        flexDirection={"column"}
                                                        gap={1}
                                                    >
                                                        <img
                                                            src={skier5}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                        <img
                                                            src={speed5}
                                                            alt="Intermediate"
                                                            height={20}
                                                            width={26}
                                                        />
                                                    </Box>
                                                ),
                                            },
                                        ]}
                                        min={1}
                                        max={5}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={(value) => {
                                            switch (value) {
                                                case 1:
                                                    return t("level1")
                                                case 2:
                                                    return t("level2")
                                                case 3:
                                                    return t("level3")
                                                case 4:
                                                    return t("level4")
                                                case 5:
                                                    return t("level5")
                                                default:
                                                    return value
                                            }
                                        }}
                                    />
                                    {formFieldMap.skiLevel?.required &&
                                        !skiLevelTouched[index] && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                            >
                                                {t("skiLevelRequired")}
                                            </Typography>
                                        )}
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}
            <Box
                display="flex"
                justifyContent="center"
                flexDirection={"column"}
                alignItems="center"
                mt={2}
                className="groupFormButtonBox"
            >
                <Button
                    variant="contained"
                    onClick={addUser}
                    className="checkInButton groupFormButton"
                    startIcon={<AddIcon />}
                >
                    {t("addMember")}
                </Button>

                <Button
                    disabled={!isFormComplete}
                    className="groupFormButton checkInButton"
                    sx={{
                        background: UIService.getColorByName(
                            Color.checkInPurple
                        ),
                        color: "white",
                    }}
                    onClick={handleNext}
                >
                    {t("Next")}
                    <CwIcon
                        name="nextIcon"
                        type="icon"
                        config={{
                            name: "arrow_forward_rounded_icon",
                            color: Color.white,
                        }}
                    ></CwIcon>
                </Button>
            </Box>
            <Dialog open={deleteModalOpen} onClose={cancelDelete}>
                <DialogTitle>{t("confirmDelete")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("deleteConfirmationMessage")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} color="error">
                        {t("cancel")}
                    </Button>
                    <Button
                        onClick={confirmDeleteUser}
                        color="success"
                        autoFocus
                    >
                        {t("confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default CwGroupNestedList
